import { supabase } from "./supabase";

/** One member in a missed-welcome review (migrations 2026-10-06-350, 2026-10-22-983). */
export interface MemberCatchupCandidate {
  contact_id: string;
  name: string;
  email: string | null;
  /** Earliest ACTIVE subscription start for one of the journey's start plans. */
  started_on: string | null;
  prior_matching_email_at: string | null;
  exclusion: string | null;
  /**
   * Other enabled member journeys this member is already in, or would also be
   * approvable in for the same range (migration 2026-10-06-352). A warning only:
   * the member stays selectable. Null when there is no overlap.
   */
  overlap?: MemberCatchupOverlap | null;
}
export interface MemberCatchupOverlap {
  already_in: string[];
  also_qualifies: string[];
  kind: "already_in" | "also_qualifies" | "both";
}
/**
 * A started paged review (migration 2026-10-22-983). The member list is frozen
 * when the review starts: `member_count` is the PRELIMINARY count (plan start in
 * range, not already in or checked by this journey); each member is checked
 * for eligibility only when their page is opened.
 */
export interface MemberCatchupReview {
  review_id: string;
  sequence_id: string;
  date_from: string;
  date_to: string;
  expires_at: string;
  start_plans: string[];
  page_size: number;
  member_count: number;
  /** Started in the range but already enrolled in, or evaluated by, this journey. Not listed. */
  already_handled: number;
  page_count: number;
}
/** One opened page: an ordinary review batch of at most 100 members. */
export interface MemberCatchupPage {
  review_id: string;
  /** The batch an approval of THIS page consumes. */
  batch_id: string;
  /** When this page's batch expires (never after the review). */
  expires_at: string;
  review_expires_at: string;
  start_plans: string[];
  date_from: string;
  date_to: string;
  page: number;
  page_count: number;
  page_size: number;
  member_count: number;
  already_handled: number;
  /** Members of the frozen list on this page. */
  evaluated: number;
  /** Hold a start plan but do not meet this journey's criteria. Not listed. */
  not_in_journey: number;
  /** An earlier active start-plan subscription, or no active one any more. Not listed. */
  not_new_start: number;
  /** Contact deleted since the review started. Not listed. */
  contact_removed: number;
  eligible: number;
  excluded: number;
  /** Listed members on this page with an overlap in another member journey (352). */
  overlap_count: number;
  /** Page numbers of this review opened so far (including this one). */
  opened_pages: number[];
  /** Members enrolled through any page of this review so far. */
  enrolled_in_review: number;
  candidates: MemberCatchupCandidate[];
}
export interface MemberCatchupResult {
  contact_id: string;
  outcome: string;
}

/** Freeze the members to review for a date range (enrolls nobody, sends nothing). */
export async function startMemberCatchupReview(
  sequenceId: string,
  from: string,
  to: string,
): Promise<MemberCatchupReview> {
  const { data, error } = await supabase.rpc(
    "start_member_journey_catchup_review",
    { p_sequence: sequenceId, p_from: from, p_to: to },
  );
  if (error) {
    throw error;
  }
  return data as MemberCatchupReview;
}

/** Check one page (<= 100 members) of a started review (enrolls nobody). */
export async function getMemberCatchupPage(
  reviewId: string,
  page: number,
): Promise<MemberCatchupPage> {
  const { data, error } = await supabase.rpc(
    "get_member_journey_catchup_page",
    { p_review: reviewId, p_page: page },
  );
  if (error) {
    throw error;
  }
  return data as MemberCatchupPage;
}

export async function approveMemberCatchup(
  batchId: string,
  contacts: string[],
): Promise<MemberCatchupResult[]> {
  const { data, error } = await supabase.rpc("approve_member_journey_catchup", {
    p_batch: batchId,
    p_contacts: contacts,
  });
  if (error) {
    throw error;
  }
  return data as MemberCatchupResult[];
}

/**
 * The plans whose start date the review uses, or null when the journey does not
 * have "Review missed welcomes" enabled. Readable by admins/superadmins only
 * (RLS); anyone else gets null, and the RPCs refuse them regardless.
 */
export async function getMemberCatchupStartPlans(
  sequenceId: string,
): Promise<string[] | null> {
  const { data, error } = await supabase
    .from("member_journey_catchup_settings")
    .select("start_plans")
    .eq("sequence_id", sequenceId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data as { start_plans: string[] } | null)?.start_plans ?? null;
}
